import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";
import { AxiosProgressEvent } from "axios";
import { CONSUMPTION_CSV_UPLOAD_ERROR_MESSAGE } from "../../Constant";

interface ConsumptionState {
  isLoading: boolean;
  data: any;
  error: string | null;
}

const initialState: ConsumptionState = {
  isLoading: false,
  data: null,
  error: null,
};

export const fetchConsumptionListData = createAsyncThunk<
  any,
  { search: string; tab: number }
>("ConsumptionSlice/fetchConsumptionListData", async (payload, thunkApi) => {
  const { search, tab } = payload;
  try {
    const response =
      tab === 0
        ? await httpClient.post("/db/consumption/list", { search })
        : await httpClient.post("/db/consumption/submitted/list", { search });
    if (response.status !== 200 || response.data.Status !== "success") {
      return thunkApi.rejectWithValue({
        message: response.data.Message || "Failed to get consumption data",
      });
    }
    return response.data.Data;
  } catch (error: any) {
    return thunkApi.rejectWithValue({
      message: error?.message || "Failed to get consumption data",
    });
  }
});

export const callProcessTransaction = createAsyncThunk<
  any,
  { TransactionId: number; Quantity: number }[]
>("ConsumptionSlice/callProcessTransaction", async (payload, thunkApi) => {
  try {
    const response = await httpClient.post("/db/consumption", payload);
    if (response.status !== 200 || response.data.Status !== "success") {
      return thunkApi.rejectWithValue({
        message: response.data.Message || "Failed to process transaction",
      });
    }
    return response.data;
  } catch (error: any) {
    if (error.status == 400) {
      return thunkApi.rejectWithValue({
        message: error.response.data.Message || "Failed to process transaction",
      });
    }
    return thunkApi.rejectWithValue({
      message: error?.message || "Failed to process transaction",
    });
  }
});

export const fetchConsumptionExportData = createAsyncThunk<
  any,
  { Action: string; Search: string }
>("ConsumptionSlice/fetchConsumptionExportData", async (payload, thunkApi) => {
  try {
    const response = await httpClient.post("/db/consumption/export", payload);
    if (response.status !== 200) {
      return thunkApi.rejectWithValue({
        message:
          response?.data?.Message || "Failed to get consumption export data",
      });
    }
    return response.data;
  } catch (error: any) {
    return thunkApi.rejectWithValue({
      message: error?.message || "Failed to get consumption export data",
    });
  }
});

async function convertCsvToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string" && result.startsWith("data:")) {
        const base64 = result.split(",")[1];
        resolve(base64);
      } else {
        reject(new Error("Unexpected file read result"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
}

export const uploadTransactionCSV = createAsyncThunk<
  any,
  { file: File; onProgress?: (percent: number) => void }
>(
  "ConsumptionSlice/uploadTransactionCSV",
  async ({ file, onProgress }, thunkApi) => {
    try {
      const base64 = await convertCsvToBase64(file);
      const response = await httpClient.post(
        "/db/consumption/upload",
        { file: base64 },
        {
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (onProgress && progressEvent.total) {
              const percent = Math.floor(
                (progressEvent.loaded / progressEvent.total) * 100,
              );
              onProgress(percent);
            }
          },
        },
      );
      if (response.status !== 200 || response.data.Status !== "success") {
        return thunkApi.rejectWithValue({
          message: CONSUMPTION_CSV_UPLOAD_ERROR_MESSAGE,
        });
      }
      return response.data;
    } catch (error: any) {
      // Check if response status is 400 and has error message
      if (error.response?.status === 400) {
        let errorMessage = error.response?.data?.Message;
        errorMessage = errorMessage.replace(
          /consumption records \[consumption record (\d+)(?:, .*?)?\]/,
          "ConsumptionRecId $1",
        );
        return thunkApi.rejectWithValue({
          message: errorMessage,
        });
      }

      return thunkApi.rejectWithValue({
        message: CONSUMPTION_CSV_UPLOAD_ERROR_MESSAGE,
      });
    }
  },
);

const ConsumptionSlice = createSlice({
  name: "ConsumptionSlice",
  initialState,
  reducers: {
    resetConsumptionState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsumptionListData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchConsumptionListData.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = action.payload;
          state.error = null;
        },
      )
      .addCase(
        fetchConsumptionListData.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error =
            action.payload.message || "Failed to get consumption data";
        },
      );
  },
});

export const { resetConsumptionState } = ConsumptionSlice.actions;

export default ConsumptionSlice.reducer;
